import classNames from "classnames";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import AsyncSelect from "react-select/async";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { createLoadOptions } from "../../hooks/useDebounce";
import { useI18n } from "../../hooks/useTranslations";
import { useCarDriversApi } from "../../http/carDrivers/api";
import { CarDriverNoCarDepency } from "../../http/carDrivers/types";
import { useAdminCarsApi } from "../../http/cars/api";
import { CarNoCarDepency } from "../../http/cars/types";
import { useOtherCarAssignmentApi } from "../../http/otherCarAssignment/api";
import { getAssignmentTypes } from "../../http/otherCarAssignment/types";
import { useCostCentersApi } from "../../http/userCostCenters/api";
import { AuthorEditor, NestedPick, PaginationQueryWithFields } from "../../types";
import { formatDateNumeric, formatTimeStampNumeric } from "../../types/utils";
import SvgEdit from "../icons/Edit";
import { ErrorBox, InfoBox, SuccessBox, WarningBox } from "../ui/Boxes";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Icon from "../ui/Icon";
import ListItemMenu, { Action } from "../ui/ListItemMenu";
import LoadingSpinner from "../ui/LoadingSpinner";
import ModalDialog from "../ui/ModalDialog";
import reactSelectStyles from "../ui/ReactSelectUtils";
import Table from "../ui/Table";
import UserCostCentersDialog from "../users/UserCostCentersDialog";
import styles from "./CarAssignmentCell.module.css";

const CAR_FIELDS = ["id", "vehicleIdentificationNumber", "currentPlateNumber", "lastPlateNumber"] as const;
type CarField = (typeof CAR_FIELDS)[number];
export type CarSearch = Pick<CarNoCarDepency, CarField>;

const DRIVER_FIELDS = [
  "id",
  "driver.id",
  "driver.fullName",
  "driver.currentCostCenter",
  "startDate",
  "endDate",
] as const;
type DriverField = (typeof DRIVER_FIELDS)[number];
type CarDriverSearch = NestedPick<CarDriverNoCarDepency, DriverField>;

interface AssignmentRow {
  id: string;
  type: "carDriver" | "other";
  name: string;
  startDate: string;
  endDate?: string;
  costCenter?: string;
  userId?: string;
}

export interface CarAssignmentDialogProps {
  date: string;
  formattedDate: string;
  originalPlate: string;
  carId?: string;
  plateHint?: string;
  forcedByUser?: AuthorEditor;
  forcedAt?: string;
  assignmentForcedByUser?: AuthorEditor;
  assignmentForcedAt?: string;
  forcedAssignmentId?: string;
  forcedAssignmentName?: string;
  onSave: (car: CarSearch) => void;
  onRemoveForce: () => Promise<void>;
  onForceAssignment: (carId: string, assignmentId: string, assignmentType: "carDriver" | "other") => Promise<boolean>;
  onAssignDriver: (carId: string) => Promise<boolean>;
  onClose: () => void;
}

const CarAssignmentDialog: FC<CarAssignmentDialogProps> = ({
  date,
  formattedDate,
  originalPlate,
  carId,
  plateHint,
  forcedByUser,
  forcedAt,
  assignmentForcedByUser,
  assignmentForcedAt,
  forcedAssignmentId,
  forcedAssignmentName,
  onSave,
  onRemoveForce,
  onForceAssignment,
  onAssignDriver,
  onClose,
}) => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const carsApi = useAdminCarsApi();
  const carDriversApi = useCarDriversApi();
  const otherCarAssignmentApi = useOtherCarAssignmentApi();
  const costCenterApi = useCostCentersApi();
  const searchTerm = useRef("");
  const aborter = useRef(new AbortController());
  const defaultOptionsAborter = useRef(new AbortController());
  const selectedCarAborter = useRef(new AbortController());
  const driversAborter = useRef(new AbortController());
  const otherAssignmentsAborter = useRef(new AbortController());

  const [selectedCar, setSelectedCar] = useState<CarSearch | null>(null);
  const [defaultCarOptions, setDefaultCarOptions] = useState<CarSearch[]>([]);
  const [carLoading, setCarLoading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [costCenterUserId, setCostCenterUserId] = useState<string | null>(null);
  const [assignmentToForce, setAssignmentToForce] = useState<AssignmentRow | null>(null);
  const [isForcingAssignment, setIsForcingAssignment] = useState(false);
  const [isAssigningDriver, setIsAssigningDriver] = useState(false);
  // undefined follows the parent; null records a successful removal while its cache refreshes.
  const [savedAssignment, setSavedAssignment] = useState<
    { carId: string; assignment: AssignmentRow } | null | undefined
  >(undefined);
  const lastForceSignatureRef = useRef<string | undefined>(undefined);

  const { trigger: fetchCar } = carsApi.getCar(carId ?? "", aborter.current.signal);

  const loadOptions = createLoadOptions<CarSearch, CarField>(aborter, searchTerm, carsApi.getCars, {
    fields: CAR_FIELDS,
  });

  const driversQuery: PaginationQueryWithFields<CarDriverSearch> = { fields: DRIVER_FIELDS };
  const {
    data: driversData,
    mutate: mutateDrivers,
    isValidating: driversLoading,
  } = carDriversApi.useAdminCarDrivers(
    driversQuery,
    driversAborter.current.signal,
    selectedCar ? selectedCar.id : false,
  );

  const { data: otherAssignmentsData, isValidating: otherAssigmentLoading } =
    otherCarAssignmentApi.useAdminOtherCarAssignment({}, otherAssignmentsAborter.current.signal, selectedCar?.id);

  const assignmentRows = useMemo<AssignmentRow[]>(() => {
    const assignmentTypes = getAssignmentTypes(i18n.t);

    return [
      ...(driversData?.items ?? []).map((carDriver) => ({
        id: carDriver.id,
        type: "carDriver" as const,
        name: carDriver.driver.fullName,
        startDate: carDriver.startDate,
        endDate: carDriver.endDate,
        costCenter: carDriver.driver.currentCostCenter,
        userId: carDriver.driver.id,
      })),
      ...(otherAssignmentsData?.items ?? []).map((otherAssignment) => ({
        id: otherAssignment.id,
        type: "other" as const,
        name: assignmentTypes.find((x) => x.value === otherAssignment.type)?.label ?? otherAssignment.type,
        startDate: otherAssignment.startDate,
        endDate: otherAssignment.endDate,
        costCenter: otherAssignment.costCenter,
      })),
    ].sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
  }, [driversData, otherAssignmentsData, i18n.language]);

  const [cachedRows] = useDataInitializer(assignmentRows);

  const costCenterSwr = costCenterApi.getAdminUserCostCenters(
    costCenterUserId ?? "",
    !!costCenterUserId && !!userSettings.permissions.userCostCenters.read,
  );

  useEffect(() => {
    costCenterUserId && userSettings.permissions.userCostCenters.read && costCenterSwr.trigger();
  }, [costCenterUserId]);

  useEffect(() => {
    defaultOptionsAborter.current.abort();
    defaultOptionsAborter.current = new AbortController();
    carsApi
      .getCars<CarField>({ limit: 5, fields: CAR_FIELDS }, defaultOptionsAborter.current.signal)
      .then(setDefaultCarOptions);

    if (carId) {
      setCarLoading(true);
      fetchCar().then((car) => {
        const carSearch: CarSearch | null = car
          ? {
              id: car.id,
              vehicleIdentificationNumber: car.vehicleIdentificationNumber,
              currentPlateNumber: car.currentPlateNumber,
              lastPlateNumber: car.lastPlateNumber,
            }
          : null;
        setSelectedCar(carSearch);
        setCarLoading(false);
      });
    } else if (plateHint) {
      setCarLoading(true);
      selectedCarAborter.current.abort();
      selectedCarAborter.current = new AbortController();
      carsApi
        .getCars<CarField>({ limit: 1, q: plateHint, fields: CAR_FIELDS }, selectedCarAborter.current.signal)
        .then((cars) => {
          setSelectedCar(cars[0] ?? null);
          setCarLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const signature = `${assignmentForcedByUser?.id ?? ""}|${assignmentForcedAt ?? ""}|${forcedAssignmentId ?? ""}`;
    if (lastForceSignatureRef.current !== undefined && lastForceSignatureRef.current !== signature) {
      setSavedAssignment(undefined);
    }
    lastForceSignatureRef.current = signature;
  }, [assignmentForcedByUser?.id, assignmentForcedAt, forcedAssignmentId]);

  const handleSave = () => {
    if (!selectedCar) return;
    onClose();
    onSave(selectedCar);
  };

  const handleRemoveForce = async () => {
    setIsRemoving(true);
    try {
      await onRemoveForce();
    } finally {
      setIsRemoving(false);
      onClose();
    }
  };

  const handleConfirmForceAssignment = async () => {
    if (!assignmentToForce || !selectedCar) return;
    setIsForcingAssignment(true);
    try {
      const saved = await onForceAssignment(selectedCar.id, assignmentToForce.id, assignmentToForce.type);
      if (saved) {
        setSavedAssignment({ carId: selectedCar.id, assignment: assignmentToForce });
        setAssignmentToForce(null);
      }
    } finally {
      setIsForcingAssignment(false);
    }
  };

  const handleAssignDriver = async () => {
    if (!selectedCar) return;
    setIsAssigningDriver(true);
    try {
      const saved = await onAssignDriver(selectedCar.id);
      if (saved) setSavedAssignment(null);
    } finally {
      setIsAssigningDriver(false);
    }
  };

  const coversDate = (assignment: AssignmentRow) => {
    const time = new Date(date).getTime();
    const start = new Date(assignment.startDate).getTime();
    const end = assignment.endDate ? new Date(assignment.endDate).getTime() : undefined;
    return time >= start && (end === undefined || time <= end);
  };

  const isEligible = (assignment: AssignmentRow) => coversDate(assignment) && !!assignment.costCenter;
  const missingRowCostCenter = (assignment: AssignmentRow) => coversDate(assignment) && !assignment.costCenter;
  const hasForcedAssignment =
    savedAssignment === undefined
      ? selectedCar?.id === carId && !!assignmentForcedByUser && !!assignmentForcedAt && !!forcedAssignmentId
      : !!savedAssignment && savedAssignment.carId === selectedCar?.id;
  const currentForcedAssignmentId = savedAssignment?.assignment.id ?? forcedAssignmentId;
  const currentForcedAssignmentName = savedAssignment?.assignment.name ?? forcedAssignmentName;
  const isForcedRow = (assignment: AssignmentRow) => hasForcedAssignment && assignment.id === currentForcedAssignmentId;

  const assignmentsLoaded = !!driversData && !!otherAssignmentsData;
  const validAssignment = cachedRows?.find(coversDate);
  const missingAssigmentCostCenter = !!validAssignment && !validAssignment.costCenter;
  const noAssignmentCoversDate = assignmentsLoaded && !validAssignment;

  const isCarForced =
    !!selectedCar &&
    (selectedCar.currentPlateNumber || selectedCar.lastPlateNumber) !== originalPlate &&
    !!forcedByUser &&
    !!forcedAt;

  const hasForcedCar = !!forcedByUser && !!forcedAt;
  const canManageAssignment = !!selectedCar;
  const isBusy =
    carLoading || isRemoving || otherAssigmentLoading || driversLoading || isForcingAssignment || isAssigningDriver;

  const actions = [];

  if (hasForcedCar) {
    actions.push({
      title: i18n.t.costsImport.removeForcedCar,
      onClick: handleRemoveForce,
      type: "secondary" as const,
      alignment: "left" as const,
      disabled: isBusy,
    });
  }
  actions.push({
    title: i18n.t.common.cancel,
    onClick: onClose,
    type: "secondary" as const,
    disabled: isBusy,
  });
  actions.push({
    title: i18n.t.common.save,
    onClick: handleSave,
    disabled: isBusy || !selectedCar || noAssignmentCoversDate || missingAssigmentCostCenter || hasForcedCar,
    type: "primary" as const,
  });

  return (
    <>
      <ModalDialog
        onClose={onClose}
        title={i18n.t.costsImport.changeCarAssignment}
        isSubmitting={isRemoving}
        isOnlyTextDialog
        actions={actions}
      >
        <FormFieldsContainer className={styles.noWidth}>
          <WarningBox text={i18n.t.costsImport.changeCarAssignmentDisclaimer(originalPlate, formattedDate)} />
          {carLoading ? (
            <div className={styles.carLoading}>
              <LoadingSpinner />
            </div>
          ) : (
            <>
              {isCarForced && (
                <InfoBox
                  text={i18n.t.costsImport.carForcedFromTo(
                    originalPlate,
                    selectedCar.currentPlateNumber ||
                      selectedCar.lastPlateNumber ||
                      selectedCar.vehicleIdentificationNumber,
                    forcedByUser.fullName,
                    formatTimeStampNumeric(forcedAt, i18n.language),
                  )}
                />
              )}
              <FormField label={i18n.t.cars.singular} fullWidth>
                <AsyncSelect
                  value={selectedCar}
                  defaultOptions={defaultCarOptions}
                  loadOptions={loadOptions}
                  placeholder={`${i18n.t.common.select}...`}
                  getOptionLabel={(x: CarSearch) =>
                    x.currentPlateNumber || x.lastPlateNumber || x.vehicleIdentificationNumber
                  }
                  getOptionValue={(x: CarSearch) => x.id}
                  styles={reactSelectStyles<CarSearch>(false)}
                  isDisabled={isBusy}
                  onChange={(v) => setSelectedCar(v)}
                  menuPosition="fixed"
                />
              </FormField>
            </>
          )}
          {selectedCar && (
            <>
              <FormField label={i18n.t.drivers.plural} isOptional fullWidth>
                <Table<AssignmentRow>
                  hoverTr={false}
                  head={
                    <>
                      <th align="left">{i18n.t.common.name}</th>
                      <th align="center">{i18n.t.common.startDate}</th>
                      <th align="center">{i18n.t.common.endDate}</th>
                      <th align="center">{i18n.t.common.costCenter}</th>
                      {canManageAssignment && <th></th>}
                    </>
                  }
                  renderRow={(assignment) => {
                    const mutedStyle = isForcedRow(assignment)
                      ? undefined
                      : hasForcedAssignment || !coversDate(assignment)
                        ? { color: "var(--text-disabled)" }
                        : undefined;
                    const rowMenuActions: Action[] = isForcedRow(assignment)
                      ? [
                          {
                            title: i18n.t.costsImport.removeAssignment,
                            onClick: handleAssignDriver,
                            isDisabled: isBusy,
                          },
                        ]
                      : coversDate(assignment)
                        ? []
                        : [
                            {
                              title: i18n.t.costsImport.forceThisDriver,
                              onClick: () => setAssignmentToForce(assignment),
                              isDisabled: isBusy,
                            },
                          ];
                    return (
                      <>
                        <td style={mutedStyle}>{assignment.name}</td>
                        <td align="center" style={mutedStyle}>
                          {formatDateNumeric(assignment.startDate, i18n.language)}
                        </td>
                        <td align="center" style={mutedStyle}>
                          {assignment.endDate ? formatDateNumeric(assignment.endDate, i18n.language) : ""}
                        </td>
                        <td align="center" style={mutedStyle}>
                          {assignment.costCenter}
                          {missingRowCostCenter(assignment) && assignment.userId && (
                            <Icon
                              glyph={SvgEdit}
                              className={classNames(styles.editIcon, styles.editIconVisible)}
                              onClick={() => setCostCenterUserId(assignment.userId!)}
                            />
                          )}
                        </td>
                        {canManageAssignment && (
                          <td style={{ padding: 0 }} onClick={(e) => e.stopPropagation()}>
                            {rowMenuActions.length > 0 && <ListItemMenu item={assignment} actions={rowMenuActions} />}
                          </td>
                        )}
                      </>
                    );
                  }}
                  getRowStyle={(assignment) =>
                    isForcedRow(assignment)
                      ? ({
                          "--row-bg": "var(--yellow-20)",
                          "--row-hover-bg": "var(--yellow-20)",
                        } as React.CSSProperties)
                      : isEligible(assignment)
                        ? ({
                            "--row-bg": "var(--green-20)",
                            "--row-hover-bg": "var(--green-20)",
                          } as React.CSSProperties)
                        : missingRowCostCenter(assignment)
                          ? ({
                              "--row-bg": "var(--red-20)",
                              "--row-hover-bg": "var(--red-20)",
                            } as React.CSSProperties)
                          : undefined
                  }
                  items={cachedRows}
                />
              </FormField>
              {assignmentsLoaded &&
                (hasForcedAssignment ? (
                  <WarningBox
                    text={`${i18n.t.costsImport.costWillBeAssignedTo(currentForcedAssignmentName ?? originalPlate)} ${i18n.t.costsImport.forcedAssignmentDoesNotCoverDate}`}
                  />
                ) : !validAssignment ? (
                  <ErrorBox text={i18n.t.costsImport.noDriverCoversDate} />
                ) : missingAssigmentCostCenter ? (
                  <ErrorBox text={i18n.t.costsImport.missingAssignmentCostCenter(validAssignment.name)} />
                ) : (
                  <SuccessBox text={i18n.t.costsImport.costWillBeAssignedTo(validAssignment.name)} />
                ))}
            </>
          )}
        </FormFieldsContainer>
      </ModalDialog>
      {costCenterUserId && costCenterSwr.data && (
        <UserCostCentersDialog
          userCostCentersWithErrors={{ ...costCenterSwr.data, userId: costCenterUserId }}
          onClose={() => setCostCenterUserId(null)}
          onSave={() => {
            setCostCenterUserId(null);
            mutateDrivers();
          }}
        />
      )}
      {assignmentToForce && (
        <ModalDialog
          onClose={() => setAssignmentToForce(null)}
          title={i18n.t.costsImport.forceThisDriver}
          isSubmitting={isForcingAssignment}
          isOnlyTextDialog
          actions={[
            {
              title: i18n.t.common.cancel,
              type: "secondary",
              disabled: isForcingAssignment,
              onClick: () => setAssignmentToForce(null),
            },
            {
              title: i18n.t.common.yes,
              disabled: isForcingAssignment,
              onClick: handleConfirmForceAssignment,
            },
          ]}
        >
          {i18n.t.costsImport.questions.forceDriver(assignmentToForce.name)}
        </ModalDialog>
      )}
    </>
  );
};

export default CarAssignmentDialog;
